import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { addMonths } from "@talentkontor/domain";
import { ErrorState, Loading } from "../../components/States";
import { PillView } from "../../components/ui";
import { api } from "../../data";
import { currencyOptionLabel, money, moneyWhole } from "../../lib/format";
import { useUnreadData } from "../kommunikation/nachrichten/hooks";
import { useContracts, useCreatorBase, useFxRates, useProfileExtras } from "./data";
import {
  claimsTaxNote, computeCreatorOverview, creatorNextSteps, creatorOccurrences, creatorRules, dealStatusPill, dueLabel, fxConverter, openClaims, pill, platformLabel, profileGaps,
  rangeOf, talentStateContext, taxIdsLabel, taxIdThresholdText, viewTalentInvoice, type RangeKey, type Step, type TalentInvoiceView,
} from "./model";
import { loadTaxHistory, savePreferences } from "./actions";
import { DemoNote, MonthChart, RankRows, useCreatorAction, useCreatorContext, useCreatorVisibility } from "./ui";
import { dateOf, monthOf, useLang, useT } from "./sprache";
import { firstSteps, taxStatusGiven } from "./einstieg";
import { ErsteSchritte } from "./Einfuehrung";
import { ProvisionZahlen } from "./Provision";
import { checkSmallBusiness, limitFootnote, payoutForecast, plannedSwitchToSmallBusiness, revenueByYear, type RevenueRow } from "./geld";
import { showsBudget } from "./sicht";
import { useNurLesen } from "../../app/nurLesen";
import { useLogoSrc } from "../../components/shell/branding";

/**
 * Übersicht des Creators (12_app_creator/uebersicht.html). Nachprüfung: nächste Schritte und Erste Schritte oben
 * (Befunde 7, 25), eine erklärte Zahl je Begriff mit der Kette „ausgezahlt · Rechnung liegt vor · Rechnung fehlt ·
 * geplant“ (Befunde 1, 16), „Wann kommt dein Geld?“, Provisionsrechnungen (Befund 14), Jahresumsatz über alle Agenturen.
 */

const RANGE_KEYS: RangeKey[] = ["6m", "ytd", "12m", "24m", "prev", "all", "custom"];

export function Uebersicht() {
  const { ws, scope, today, userId, lang } = useCreatorContext();
  const t = useT();
  const visibility = useCreatorVisibility();
  const nurLesen = useNurLesen();
  const base = useCreatorBase(scope);
  const contracts = useContracts(scope);
  const profile = useProfileExtras(scope, userId);
  const fx = useFxRates();
  const unread = useUnreadData({ portal: "creator", orgId: scope.orgId, talentId: scope.talentId });
  // CREATOR-R3-7: geplanter Wechsel zur Kleinunternehmerregelung (Verlauf mit Gilt-ab in der Zukunft)
  const taxHistory = useQuery({ queryKey: ["tax_status_history", scope.talentId], queryFn: () => loadTaxHistory(api, scope.talentId) });
  const revenue = useQuery({ queryKey: ["creator-revenue-years", userId], enabled: !!userId, staleTime: 5 * 60_000, queryFn: () => api.read<RevenueRow[]>("my_revenue_by_year", {}).catch(() => [] as RevenueRow[]) });
  const [currency, setCurrency] = useState(ws.org.currency);
  const [rangeKey, setRangeKey] = useState<RangeKey>("6m");
  const cur = today.slice(0, 7);
  const [custom, setCustom] = useState({ from: addMonths(cur, -5), to: cur });
  const { run, busy } = useCreatorAction();

  const b = base.data;
  const firstName = b ? (b.talent.nickname?.trim() || b.talent.first_name) : ws.talentName.split(" ")[0];

  const computed = useMemo(() => {
    if (!b) return null;
    const rules = creatorRules(b.defaults, b.org);
    const ctx = talentStateContext(today, rules, b.org.currency);
    const views = b.invoices.map((r) => viewTalentInvoice(r, ctx, b.org.timezone, lang, rules)).filter((v): v is TalentInvoiceView => !!v);
    const occurrences = creatorOccurrences({
      today, talent: b.talent, deals: b.deals, periods: b.periods, lineItems: b.lineItems, invoices: b.invoices, bookings: b.bookings, rates: b.rates, agencyInvoices: b.agencyInvoices,
    });
    const months = occurrences.map((o) => o.month).sort();
    const range = rangeOf(rangeKey, today, { earliest: months[0] ?? null, latest: months.at(-1) ?? null, from: custom.from, to: custom.to }, lang);
    const overview = computeCreatorOverview({
      today, range, currency, occurrences, deals: b.deals, periods: b.periods, convert: fxConverter(fx.data ?? []), chartAhead: rangeKey === "all" ? 0 : 2, invoices: b.invoices,
    });
    return { rules, views, overview, range, occurrences, currencies: [...new Set([b.org.currency, ...b.deals.map((d) => d.currency)])] };
  }, [b, today, rangeKey, custom, currency, fx.data, lang]);

  if (base.error) return <ErrorState error={base.error} retry={() => void base.refetch()} />;
  if (base.isPending || !b || !computed) return <Loading />;

  const { overview: o, views, range, rules, occurrences } = computed;
  const m = (c: number) => moneyWhole(c, currency);
  const selfBilling = !!b.talent.self_billing_agreed_on;
  const gaps = profileGaps(b.talent, profile.data?.payout ?? null, rules);
  const steps = creatorNextSteps({
    today, deals: b.deals, periods: b.periods, brands: b.brands, views,
    contracts: contracts.data?.contracts, signers: contracts.data?.signers, profileMissing: profile.data ? gaps.blocking : [], lang,
    selfBilling, agencyInvoices: b.agencyInvoices, money: (c, cy) => money(c, cy),
    deliverables: b.deliverables, platformLabel: (k) => platformLabel(k, lang, b.platformNames), readOnly: nurLesen,
  });
  const tasks = steps.filter((s) => s.kind !== "info");
  const running = steps.filter((s) => s.kind === "info");
  const claims = openClaims(views);
  const claimCents = claims.byCurrency[currency] ?? 0;
  const otherClaimCurrencies = Object.keys(claims.byCurrency).filter((c) => c !== currency);
  const brandName = (id: string) => b.brands.get(id)?.name ?? t("Marke", "Brand");
  const prefs = profile.data?.prefs ?? null;
  const taxHint = !b.talent.small_business && !b.talent.tax_number?.trim() && !b.talent.vat_id?.trim() && profile.data && !prefs?.onboarding?.tax_hint_dismissed;
  const thisMonth = monthOf(o.current, lang);
  const taxLabel = taxIdsLabel(b.talent.country, lang);
  const budgetVisible = showsBudget(visibility);
  const bellCount = unread.data?.count.total ?? 0;
  const setup = profile.data ? firstSteps({ talent: b.talent, payout: profile.data.payout, hasSignature: !!profile.data.signature, invoices: b.invoices, rules, lang }) : null;
  const forecast = payoutForecast({
    today, views, deals: b.deals, periods: b.periods, occurrences, brandName, payoutAfterBrandPayment: rules.payoutAfterBrandPayment, selfBilling, visibility, lang, timeZone: b.org.timezone,
  });
  const years = revenueByYear(revenue.data ?? []);
  const year = Number(today.slice(0, 4));
  const taxStatusMissing = !taxStatusGiven(b.talent);
  const plannedFrom = plannedSwitchToSmallBusiness(taxHistory.data ?? [], today, b.talent.small_business);
  const limit = checkSmallBusiness({ smallBusiness: b.talent.small_business, country: b.talent.country, year, currentYear: year, totals: years, lang, money: (c, cy) => moneyWhole(c, cy), taxStatusMissing, plannedSmallBusinessFrom: plannedFrom });
  const limitPrev = checkSmallBusiness({ smallBusiness: b.talent.small_business, country: b.talent.country, year: year - 1, currentYear: year, totals: years, lang, money: (c, cy) => moneyWhole(c, cy), taxStatusMissing });

  async function dismissTaxHint() {
    if (!userId) return;
    await run("hinweis", () => savePreferences(api, userId, { onboarding: { ...(prefs?.onboarding ?? {}), tax_hint_dismissed: true } }, !!prefs));
  }
  const stepLink = (s: Step) => (
    <li key={s.key}>
      <Link to={s.to} className="tk-schritt-link">
        <strong>{s.title}</strong>
        <PillView pill={s.kind === "info" ? pill(t("Läuft", "In progress"), "neutral") : s.due ? pill(dueLabel(s.due, today, lang), s.tone) : pill(t("Offen", "Open"), s.tone)} />
        <small>{s.detail}</small>
      </Link>
    </li>
  );

  // Paket 2: Währung und Zeitraum stehen wie im Original (und wie in der Agentur-Übersicht) im Seitenkopf
  const geldFilter = (
    <div className="overview-filter">
      <label>
        {t("Währung", "Currency")}
        <select aria-label={t("Währung", "Currency")} value={currency} onChange={(e) => setCurrency(e.target.value)}>
          {computed.currencies.map((c) => <option key={c} value={c}>{currencyOptionLabel(c, "de-DE")}</option>)}
        </select>
      </label>
      <label>
        {t("Zeitraum", "Period")}
        <select aria-label={t("Zeitraum", "Period")} value={rangeKey} onChange={(e) => setRangeKey(e.target.value as RangeKey)}>
          {RANGE_KEYS.map((k) => (
            <option key={k} value={k}>
              {k === "custom" ? t("Eigener Zeitraum", "Custom period") : k === "all" ? t("Gesamt", "All time") : rangeOf(k, today, {}, lang).label}
            </option>
          ))}
        </select>
      </label>
      {rangeKey === "custom" && (
        <>
          <label>
            {t("Von", "From")}
            <input type="month" value={custom.from} max={cur} onChange={(e) => e.target.value && setCustom((c) => ({ ...c, from: e.target.value }))} />
          </label>
          <label>
            {t("Bis", "To")}
            <input type="month" value={custom.to} onChange={(e) => e.target.value && setCustom((c) => ({ ...c, to: e.target.value }))} />
          </label>
        </>
      )}
      <small>{t("Stand", "As of")} {dateOf(today, lang)}</small>
    </div>
  );

  return (
    <main id="main" className="content tk-cr tk-cr-uebersicht">
      <div className="pagehead">
        <div>
          <h1>{t("Übersicht", "Overview")}</h1>
          <p>{t(`Hallo, ${firstName}. Deine nächsten Schritte, dein Geld und deine Kooperationen.`, `Hello, ${firstName}. Your next steps, your money and your collaborations.`)}</p>
          {/* Agentur als Inhalt, nicht als Rahmen: oben links steht immer CreatorOffice (Marke 29.09.2026) */}
          <DeineAgentur name={ws.org.name} logoPath={ws.org.logo_path} label={t("Deine Agentur", "Your agency")} />
        </div>
        {geldFilter}
      </div>

      {setup && <ErsteSchritte steps={setup} userId={userId} />}

      {/* Befund 25: Aufgaben gleich unter der Begrüßung, mit Marke, Monat und Betrag */}
      <section className="panel stack" aria-labelledby="naechste-schritte">
        <h2 id="naechste-schritte">{t("Deine nächsten Schritte", "Your next steps")}</h2>
        {tasks.length > 0 ? <ul className="tk-tasks">{tasks.map(stepLink)}</ul>
          : <p>{t("Für heute ist alles erledigt. Neue Aufgaben erscheinen hier automatisch.", "Everything is done for today. New tasks appear here automatically.")}</p>}
        {running.length > 0 && (
          <>
            <div className="group-label">{t("Gerade läuft, nichts zu tun", "In progress, nothing to do")}</div>
            <ul className="tk-tasks tk-info">{running.map(stepLink)}</ul>
          </>
        )}
        {/* Befund 26: Glocke und nächste Schritte gehören zusammen */}
        {bellCount > 0 && (
          <p><Link to="/creator/benachrichtigungen">{bellCount === 1 ? t("1 neuer Hinweis in deiner Glocke", "1 new note in your bell") : t(`${bellCount} neue Hinweise in deiner Glocke`, `${bellCount} new notes in your bell`)} ›</Link></p>
        )}
      </section>

      {taxHint && (
        <div className="notice stack tk-gap-sm" role="status">
          <p><strong>{t(`${taxLabel} fehlt.`, `${taxLabel} missing.`)}</strong> {t(`Ohne diese Angabe fragt deine Agentur ${taxIdThresholdText(b.talent.country, lang)} nach. Einreichen kannst du trotzdem.`, `Without it your agency will ask ${taxIdThresholdText(b.talent.country, lang)}. You can still submit.`)}</p>
          <div className="row">
            <Link className="btn" to="/creator/profil">{t("Profil ergänzen", "Complete profile")}</Link>
            <button onClick={() => void dismissTaxHint()} disabled={busy === "hinweis"}>{t("Nicht mehr anzeigen", "Don't show again")}</button>
          </div>
        </div>
      )}

      <ProvisionZahlen invoices={b.agencyInvoices} orgId={scope.orgId} orgName={b.org.name} today={today} />

      {/* „Dein Geld“ leitet die Kacheln ein; die Auswahl dazu steht oben im Seitenkopf */}
      <div className="group-label tk-cr-geld">{t("Dein Geld", "Your money")}</div>

      <section>
        <div className="group-label">{t("Zeitraum-unabhängig · netto, dein Anteil", "Independent of period · net, your share")}</div>
        <div className="overview-kpis talent-kpis total">
          <div className="kpi">
            <small>{t("Verdient diesen Monat", "Earned this month")} <span className="kpi-chip">{thisMonth}</span></small>
            <div className="metric num">{m(o.monthEarnedCents)}</div>
            <small>{t("Dein Anteil aus Leistungen dieses Monats, ausgezahlt oder noch offen.", "Your share from this month's work, paid out or still open.")}</small>
          </div>
          <div className="kpi">
            <small>{t("Verdient dieses Jahr", "Earned this year")} <span className="kpi-chip">{o.year}</span></small>
            <div className="metric num">{m(o.yearEarnedCents)}</div>
            <small>{t("Dein Anteil aus allen Leistungen dieses Jahres bei dieser Agentur.", "Your share from all work this year at this agency.")}</small>
          </div>
          <div className="kpi">
            <small>{t("Geplant", "Planned")} <span className="kpi-chip">{t("noch nicht geliefert", "not yet delivered")}</span></small>
            {/* „kein Wert“ ist nicht 0: ohne bekannten Anteil steht ein Strich (Arbeitsregel 6) */}
            <div className="metric num">{o.forecastCount === 0 && o.unknownPipeline > 0 ? "–" : m(o.forecastCents)}</div>
            <small>{t("Dein erwarteter Anteil aus Monaten, die noch kommen oder noch laufen.", "Your expected share from months still to come or still running.")}</small>
            {o.unknownPipeline > 0 && (
              <div className="kpi-alert">
                {o.unknownPipeline === 1 ? t("1 geplanter Monat", "1 planned month") : t(`${o.unknownPipeline} geplante Monate`, `${o.unknownPipeline} planned months`)} {t("ohne hinterlegten Provisionssatz · dein Anteil steht mit deiner Rechnung fest", "without a commission rate on file · your share is fixed with your invoice")}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Befund 16: eine Kette, jede Zahl einmal und erklärt; alles netto */}
      <section>
        <div className="group-label">{t("Im Zeitraum", "In the period")} · {range.label} · {t(`verdient ${m(o.period.earnedCents)} netto`, `earned ${m(o.period.earnedCents)} net`)}</div>
        <div className="overview-kpis period tk-chain">
          <div className="kpi">
            <small>{t("Ausgezahlt", "Paid out")} <span className="kpi-chip">{range.short}</span></small>
            <div className="metric num">{m(o.period.paidCents)}</div>
            <small>{t("Ist bei dir angekommen bzw. von deiner Agentur gebucht.", "Has reached you or been booked by your agency.")}</small>
          </div>
          <div className="kpi">
            <small>{t("Rechnung liegt vor, Auszahlung offen", "Invoice done, payout open")} <span className="kpi-chip">{range.short}</span></small>
            <div className="metric num">{m(o.period.openInvoicedCents)}</div>
            <small>{t("Deine Rechnung ist eingereicht; das Geld kommt nach Freigabe und Zahlungsziel.", "Your invoice is submitted; the money follows after approval and the payment term.")}</small>
          </div>
          <div className="kpi">
            <small>{selfBilling ? t("Gutschrift folgt", "Self-billed invoice to follow") : t("Rechnung fehlt noch", "Invoice still missing")} <span className="kpi-chip">{range.short}</span></small>
            <div className="metric num">{m(o.period.openMissingCents)}</div>
            <small>{selfBilling
              ? t("Geliefert; deine Agentur stellt die Gutschrift aus, danach wird ausgezahlt.", "Delivered; your agency issues the self-billed invoice, then pays out.")
              : t("Geliefert, aber ohne deine Rechnung. Ohne Rechnung zahlt deine Agentur nicht aus.", "Delivered but without your invoice. Without an invoice your agency does not pay out.")}</small>
            {o.period.missingCount > 0 && !selfBilling && <div className="kpi-alert">{o.period.missingCount === 1 ? t("1 Monat ohne Rechnung", "1 month without invoice") : t(`${o.period.missingCount} Monate ohne Rechnung`, `${o.period.missingCount} months without invoice`)} · <Link to="/creator/deals">{t("ansehen", "view")}</Link></div>}
          </div>
          <div className="kpi">
            <small>{t("Geplant", "Planned")} <span className="kpi-chip">{range.short}</span></small>
            <div className="metric num">{m(o.status.pipelineCents)}</div>
            <small>{t("Noch nicht geliefert; zählt nicht zu „verdient“.", "Not yet delivered; not counted as “earned”.")}</small>
            {o.unknownInRange > 0 && <small>{o.unknownInRange === 1 ? t("1 Monat", "1 month") : t(`${o.unknownInRange} Monate`, `${o.unknownInRange} months`)} {t("ohne bekannten Anteil nicht enthalten", "without a known share not included")}</small>}
          </div>
        </div>
      </section>

      <section>
        <div className="group-label">{t("Weitere", "More")}</div>
        <div className="overview-kpis talent-kpis more">
          <div className="kpi">
            <small>{t("Noch auszuzahlen laut deinen Rechnungen", "Still to be paid per your invoices")}</small>
            <div className="metric num">{moneyWhole(claimCents, currency)}</div>
            <small>
              {claims.count === 1 ? t("1 Rechnung", "1 invoice") : t(`${claims.count} Rechnungen`, `${claims.count} invoices`)} · {claimsTaxNote(claims, b.talent, lang)}
              {claims.overdue > 0 ? t(` · ${claims.overdue} überfällig`, ` · ${claims.overdue} overdue`) : ""}
              {otherClaimCurrencies.length > 0 ? t(` · dazu offen in ${otherClaimCurrencies.join(", ")}`, ` · also open in ${otherClaimCurrencies.join(", ")}`) : ""}
            </small>
          </div>
          <div className="kpi">
            <small>{t("Aktive Kooperationen", "Active collaborations")}</small>
            <div className="metric num">{o.activeDeals}</div>
            <small>{t("Mit mindestens einem Monat, der noch nicht erledigt ist; wie „Offen“ unter „Deine Deals“.", "With at least one month not yet done; same as “Open” under “Your deals”.")}</small>
          </div>
          <div className="kpi">
            <small>{t("Top-Marke", "Top brand")} <span className="kpi-chip">{range.short}</span></small>
            <div className="metric num">{o.topBrand ? brandName(o.topBrand.brandId) : "–"}</div>
            <small>{o.topBrand ? `${m(o.topBrand.cents)} · ${t("verdient, netto", "earned, net")}` : t("Im Zeitraum ohne bekannten Anteil", "No known share in the period")}</small>
          </div>
          {budgetVisible && (
            <div className="kpi">
              <small>{t("Ø Kooperationswert", "Average collaboration value")} <span className="kpi-chip">{range.short}</span></small>
              <div className="metric num">{m(o.period.avgDealCents)}</div>
              <small>{t("Honorar der Marke je Kooperation im Zeitraum, vor Provision (dein Anteil und der deiner Agentur).", "Brand fee per collaboration in the period, before commission (your share and your agency's).")}</small>
            </div>
          )}
        </div>
      </section>

      {forecast.length > 0 && (
        <section className="panel stack" aria-labelledby="wann-geld">
          <h2 id="wann-geld">{t("Wann kommt dein Geld?", "When will you get your money?")}</h2>
          <ul className="tk-money-list">
            {forecast.slice(0, 8).map((l) => (
              <li key={l.key}>
                <Link to={l.to}>{l.label}</Link>
                <span className="num">{l.cents == null ? "–" : money(l.cents, l.currency)}{l.gross ? "" : ` ${t("netto", "net")}`}</span>
                <small>{l.text}</small>
              </li>
            ))}
          </ul>
          {forecast.length > 8 && <Link to="/creator/rechnungen">{t(`Alle ${forecast.length} offenen Beträge unter „Rechnungen“`, `All ${forecast.length} open amounts under “Invoices”`)}</Link>}
        </section>
      )}

      <div className="grid portal-charts">
        <section className="panel">
          <h3>{t("Monatliche Einnahmen (netto)", "Monthly earnings (net)")}</h3>
          <MonthChart months={o.months} currency={currency} current={o.current} />
        </section>
        <section className="panel">
          <h3>{t("Top-Marken (verdient, netto)", "Top brands (earned, net)")}</h3>
          <RankRows
            rows={o.topBrands.slice(0, 5).map((x) => ({ key: x.brandId, label: brandName(x.brandId), cents: x.cents, value: m(x.cents) }))}
            empty={t("Im gewählten Zeitraum gibt es noch keinen verdienten Anteil.", "There is no earned share in the selected period yet.")}
          />
          <small>{t("Ohne Geplantes; das steht unter „Geplant“.", "Without planned amounts; those are under “Planned”.")}</small>
        </section>
      </div>

      <div className="grid portal-charts">
        <section className="panel">
          <h3>{t("Dein Anteil nach Stand", "Your share by status")} · {range.short}</h3>
          <RankRows
            rows={[
              { key: "paid", label: t("Ausgezahlt", "Paid out"), cents: o.status.paidCents, value: m(o.status.paidCents) },
              { key: "open", label: t("Rechnung liegt vor", "Invoice done"), cents: o.status.invoicedCents, value: m(o.status.invoicedCents) },
              { key: "missing", label: selfBilling ? t("Gutschrift folgt", "Self-billed invoice to follow") : t("Rechnung fehlt noch", "Invoice still missing"), cents: o.status.missingCents, value: m(o.status.missingCents) },
              { key: "pipeline", label: t("Geplant", "Planned"), cents: o.status.pipelineCents, value: m(o.status.pipelineCents) },
            ].filter((r) => r.cents > 0)}
            empty={t("Im gewählten Zeitraum gibt es noch keinen bekannten Anteil.", "There is no known share in the selected period yet.")}
          />
        </section>
        <section className="panel">
          <h3>{t("Aktive Kooperationen nach Stand", "Active collaborations by status")}</h3>
          <RankRows
            rows={o.pipeline.map((p) => {
              const cents = budgetVisible ? p.budgetCents : p.shareCents;
              return { key: p.status, label: `${dealStatusPill(p.status, lang, b.statusLabels).label} · ${p.deals}`, cents, value: m(cents) };
            })}
            empty={t("Gerade laufen keine Kooperationen.", "No collaborations are running right now.")}
          />
          {o.pipeline.length > 0 && <small>{budgetVisible
            ? t("Honorar der Marke netto je Stand, vor Provision (dein Anteil und der deiner Agentur).", "Brand fee net per status, before commission (your share and your agency's).")
            : t("Dein noch nicht ausgezahlter Anteil je Stand, netto.", "Your share not yet paid out per status, net.")}</small>}
        </section>
      </div>

      <JahresUmsatz years={years} limit={limit} limitPrev={limitPrev} smallBusiness={b.talent.small_business || !!plannedFrom} currentYear={year} loading={revenue.isPending} />

      {o.unconverted > 0 && (
        <p className="demo-note">{o.unconverted === 1 ? t("1 Betrag in anderer Währung ohne hinterlegten Kurs ist nicht enthalten.", "1 amount in another currency without an exchange rate on file is not included.")
          : t(`${o.unconverted} Beträge in anderer Währung ohne hinterlegten Kurs sind nicht enthalten.`, `${o.unconverted} amounts in another currency without an exchange rate on file are not included.`)}</p>
      )}
      <DemoNote />
    </main>
  );
}

/** Umsätze je Jahr über alle Agenturen (auch frühere) mit Hinweis zur Kleinunternehmer-Grenze. */
function JahresUmsatz({ years, limit, limitPrev, smallBusiness, currentYear, loading }: {
  years: ReturnType<typeof revenueByYear>; limit: ReturnType<typeof checkSmallBusiness>; limitPrev: ReturnType<typeof checkSmallBusiness>;
  smallBusiness: boolean; currentYear: number; loading: boolean;
}) {
  const t = useT();
  const lang = useLang();
  if (loading) return null;
  const shown = years.filter((y) => y.year >= currentYear - 2);
  const warn = [limit, limitPrev].find((l) => l.level !== "ok");
  return (
    <section className={`panel stack${warn ? (warn.level === "over" ? " tk-limit-over" : " tk-limit-near") : ""}`} aria-labelledby="jahresumsatz">
      <h2 id="jahresumsatz">{t("Deine Umsätze je Jahr, alle Agenturen", "Your turnover per year, all agencies")}</h2>
      <p>{t("Summe deiner eigenen Rechnungen netto nach Rechnungsdatum, bei allen Agenturen, mit denen du verbunden bist oder warst. Gutschriften deiner Agenturen zählen mit.",
        "Total of your own invoices, net, by invoice date, at all agencies you are or were connected with. Self-billed invoices from your agencies count too.")}</p>
      {shown.length === 0 ? <p className="muted">{t("Noch keine Rechnungen.", "No invoices yet.")}</p> : (
        <ul className="tk-money-list">
          {shown.map((y) => (
            <li key={`${y.year}-${y.currency}`}>
              <strong>{y.year}</strong>
              <span className="num"><strong>{money(y.invoicedCents, y.currency)}</strong></span>
              <small>
                {t(`davon ausgezahlt ${money(y.paidCents, y.currency)}`, `of which paid out ${money(y.paidCents, y.currency)}`)} · {y.byAgency.map((a) => `${a.name}${a.archived ? t(" (früher)", " (former)") : ""}: ${money(a.invoicedCents, y.currency)}`).join(" · ")}
              </small>
            </li>
          ))}
        </ul>
      )}
      {smallBusiness ? (
        <>
          <p role={warn ? "status" : undefined}>{(warn ?? limit).text}</p>
          {warn && <div className="row"><Link className="btn" to="/creator/profil#steuerstatus">{t("Steuerstatus im Profil ändern", "Change tax status in profile")}</Link></div>}
        </>
      ) : limit.missing ? (
        <>
          <p>{limit.text}</p>
          <div className="row"><Link className="btn" to="/creator/profil#steuerstatus">{t("Steuerstatus angeben", "Enter your tax status")}</Link></div>
        </>
      ) : <small>{limit.text}</small>}
      <small>{limitFootnote(smallBusiness, lang)}</small>
    </section>
  );
}

/** „Deine Agentur: …“ mit Logo der Agentur (Bucket branding), sonst nur dem Namen. */
function DeineAgentur({ name, logoPath, label }: { name: string; logoPath: string | null; label: string }) {
  const logo = useLogoSrc(logoPath);
  return (
    <p className="tk-cr-agentur">
      <span className="muted">{label}:</span>
      {logo ? <img className="tk-cr-agentur-logo" src={logo} alt="" /> : null}
      <strong>{name}</strong>
    </p>
  );
}
